import { useState, useMemo, useCallback, useRef } from "react";
import { EditablePill } from "@/components/common/EditablePill";
import { AniNavScope, useAniNavTabs } from "@/components/ui/ani-nav";
import { motion, AnimatePresence } from "framer-motion";

export interface Tab {
  id: string;
  label: string;
  icon?: React.ReactNode;
  content?: React.ReactNode;
  showNotification?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  currentIcon?: string;
  currentColor?: string;
  onIconChange?: (icon: string) => void;
  onColorChange?: (color: string) => void;
  count?: number;
  /** Force dark text color, bypassing auto-contrast */
  forceTextDark?: boolean;
}

interface TabsProps {
  tabs: Tab[];
  defaultTab?: string;
  activeTab?: string;
  onChange?: (tabId: string) => void;
  actionButton?: {
    icon: React.ReactNode;
    onClick: () => void;
    label: string;
    isActive?: boolean;
  };
  /** Arbitrary ReactNode rendered in the right side of the tab header */
  rightSlot?: React.ReactNode;
  allowEdit?: boolean;
  color?: string;
  /** When true, disables icon/color editing functionality for all tabs */
  disableIconEdit?: boolean;
  onRemoveTab?: (tabId: string) => void;
  onEditModeChange?: (isEditMode: boolean) => void;
  /** When true, enables horizontal scrolling for tabs when they overflow */
  scrollable?: boolean;
  /** When true, the tabs are static and do not scroll */
  staticTabs?: boolean;
  fullWidth?: boolean;
  /** When true, Floating Ani can switch to these tabs and reach targets inside their content (`fe_show`). */
  aniNav?: boolean;
}

const itemVariants = {
  initial: { opacity: 0, scale: 0.8, y: 0 },
  animate: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.8, y: 0, transition: { duration: 0.15 } },
};

export function Tabs({
  tabs,
  defaultTab,
  activeTab,
  onChange,
  actionButton,
  rightSlot,
  allowEdit = false,
  color,
  disableIconEdit = false,
  onRemoveTab,
  onEditModeChange,
  scrollable = false,
  staticTabs = false,
  fullWidth = false,
  aniNav = false,
}: TabsProps) {
  const [internalActiveTab, setInternalActiveTab] = useState(
    defaultTab || tabs[0]?.id
  );

  // currentActiveTab is determined by activeTab if provided, otherwise internal state.
  const currentActiveTab =
    typeof activeTab === "string" ? activeTab : internalActiveTab;

  const handleTabChange = useCallback(
    (tabId: string, e?: React.MouseEvent) => {
      const tab = tabs.find((t) => t.id === tabId);

      // If the specific tab has an onClick handler, call it and then return.
      // This means the tab's onClick is responsible for any further actions, including calling the main onChange if needed.
      if (tab?.onClick && e) {
        tab.onClick(e);
        return;
      }

      // If no specific tab onClick, proceed with general tab change logic.
      if (onChange) {
        // If onChange is provided, it means the parent wants to control or be notified of tab changes.
        onChange(tabId);
        // We do NOT call setInternalActiveTab here if activeTab is managed by the parent,
        // as that would be redundant and could cause the double render.
        // Only update internal state if the component is fully uncontrolled (activeTab is undefined).
        if (typeof activeTab === "undefined") {
          setInternalActiveTab(tabId);
        }
      } else {
        // Purely uncontrolled: no onChange, no activeTab prop from parent.
        setInternalActiveTab(tabId);
      }
    },
    [tabs, onChange, activeTab, setInternalActiveTab]
  );

  const handleRemoveClick = useCallback(
    (tabId: string) => {
      // Note: stopPropagation is already handled in EditablePill's onClick
      if (onRemoveTab) {
        onRemoveTab(tabId);
      }
    },
    [onRemoveTab]
  );

  const activeContent = useMemo(
    () => tabs.find((tab) => tab.id === currentActiveTab)?.content,
    [tabs, currentActiveTab]
  );

  const tabElementsRef = useRef(new Map<string, HTMLElement>());
  useAniNavTabs({
    enabled: aniNav,
    tabs,
    activeTabId: currentActiveTab,
    onActivate: handleTabChange,
    getElement: (tabId) => tabElementsRef.current.get(tabId) ?? null,
  });

  const tabElements = useMemo(() => {
    return tabs.map((tab, index) => (
      <motion.div
        key={tab.id}
        ref={(element: HTMLDivElement | null) => {
          if (element) {
            tabElementsRef.current.set(tab.id, element);
          } else {
            tabElementsRef.current.delete(tab.id);
          }
        }}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.8, transition: { duration: 0.15 } }}
        transition={{
          type: "spring",
          stiffness: 500,
          damping: 30,
          delay: index * 0.05,
        }}
        style={{
          display: `${fullWidth ? "flex" : "inline-block"}`,
          transformOrigin: "center",
        }}
        className={`flex-none ${fullWidth ? "flex-grow flex-shrink-0 flex justify-center" : ""}`}
      >
        <EditablePill
          text={tab.label}
          icon={tab.icon}
          animateBorder={tab.showNotification || false}
          currentIcon={tab.currentIcon || ""}
          showNotification={tab.showNotification || false}
          currentColor={color || tab.currentColor}
          onIconChange={(icon) => tab.onIconChange?.(icon || "")}
          onColorChange={(color) => tab.onColorChange?.(color || "")}
          isActive={currentActiveTab === tab.id}
          disableIconEdit={disableIconEdit}
          showRemoveButton={allowEdit}
          flatOpacity={1}
          onRemove={() => handleRemoveClick(tab.id)}
          flat={true}
          onClickPill={() => handleTabChange(tab.id)}
          className={""}
          count={tab.count}
          forceTextDark={tab.forceTextDark}
        />
      </motion.div>
    ));
  }, [
    tabs,
    allowEdit,
    disableIconEdit,
    color,
    handleRemoveClick,
    handleTabChange,
    currentActiveTab,
    fullWidth,
  ]);

  return (
    <div className="flex flex-col h-full w-full group/tabs">
      {/* Tab Header */}
      <div className="px-4 pt-4">
        <div className="flex gap-1 items-center justify-between">
          <div
            className={`flex gap-3 py-4 px-1 min-h-[60px] ${
              scrollable ? "overflow-x-auto no-scrollbar" : ""
            }
              ${fullWidth ? "flex-grow" : ""}
            `}
          >
            {tabElements}
          </div>
          <div className="flex items-center gap-2 relative">
            {rightSlot}
            {actionButton && (
              <button
                onClick={actionButton.onClick}
                className={`flex items-center gap-3 px-3 hover:text-slate-900 dark:hover:text-slate-100 min-h-[32px] rounded-lg cursor-pointer relative text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150 ${
                  actionButton.isActive
                    ? "text-slate-900 dark:text-slate-100 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700"
                    : ""
                }`}
                title={actionButton.label}
              >
                {actionButton.icon}
                <span className="text-sm font-medium">
                  {actionButton.label}
                </span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div
        className={`flex-1 flex border-slate-200 dark:border-slate-700 ${
          staticTabs ? "p-4 pt-0 overflow-hidden" : " overflow-auto"
        }`}
      >
        <AnimatePresence mode="wait">
          {activeContent && (
            <motion.div
              key={currentActiveTab}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{
                duration: 0.25,
                ease: "easeInOut",
              }}
              className={`flex-1 w-full h-full min-h-0 ${
                staticTabs
                  ? "w-full overflow-y-auto rounded-2xl bg-gradient-to-bl from-slate-100 to-slate-50 text-slate-900 dark:from-slate-800/50 dark:to-slate-800/40 dark:text-white"
                  : ""
              }`}
            >
              {aniNav && currentActiveTab ? (
                <AniNavScope path={currentActiveTab}>{activeContent}</AniNavScope>
              ) : (
                activeContent
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
